import {
  Button,
  CircularProgress,
  Box,
  Switch,
  Typography
} from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import { useState, useEffect } from 'react';
import { SnackbarSeverityType, UserRole } from '../../../enums';
import {
  academicYears,
  defaultAcademicYear
} from '../../../constants/academicYears';
import { useSchoolClassesConfigHook } from '../../common/hooks/useSchoolClassesConfig';
import userApi from '../../../apis/users';
import subjectAndGroupingApis from '../../../apis/subjects';
import { TeacherProfile } from '../types';
import { ClassFormWrapper } from '../styled';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

interface AddClassStaffForm {
  staffDetailData: TeacherProfile | undefined;
  open: boolean;
  onClose: () => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onAddNewClassSuccess: () => void;
}

const AddClassForm: React.FC<AddClassStaffForm> = ({
  staffDetailData,
  open,
  onClose,
  showToast,
  onAddNewClassSuccess
}) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear || '');
  const [grade, setGrade] = useState<string>('');
  const [division, setDivision] = useState<string>('');
  const [section, setSection] = useState<string>('');
  const [subject, setSubject] = useState<string[]>([]);
  const [isClassTeacher, setIsClassTeacher] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subjectsOptions, setSubjectsOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const { loggedInUser, gradeOptions, divisionOptions, sectionOptions } =
    useSchoolClassesConfigHook();

  const handleModalClose = () => {
    onClose();
    setGrade('');
    setDivision('');
    setSection('');
    setSubject([]);
    setSubjectsOptions([]);
    setIsClassTeacher(false);
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      setSubject([]);
      if (!loggedInUser?.school || !grade || !division) return;

      setSubjectsLoading(true);

      try {
        const response = await subjectAndGroupingApis.fetchSchoolSubject(
          loggedInUser.school,
          grade,
          division
        );

        const subjects = response as any;
        const subjectOptions = subjects?.map((subject: any) => ({
          label: subject.name,
          value: subject.name
        }));

        setSubjectsOptions(subjectOptions);
      } catch (error: any) {
        setSubjectsOptions([]);
        showToast(
          error?.errors?.[0]?.message || 'Failed to fetch subjects',
          SnackbarSeverityType.error
        );
      } finally {
        setSubjectsLoading(false);
      }
    };
    fetchSubjects();
  }, [grade, division, loggedInUser?.school, showToast]);

  const addClass = async () => {
    if (!staffDetailData?.id) return;
    try {
      const payload = {
        event: 'ADD_ACADEMIC_YEAR_WISE_MAPPING',
        academicYear: academicYear,
        grade: grade,
        division: division,
        subjects: subject,
        section: section,
        isClassTeacher: isClassTeacher,
        classesToBeDeleted: [],
        activityClubs: staffDetailData?.currentCourseInfo?.activityClubs || []
      };
      await userApi.updateCourseInfo(staffDetailData?.id, payload);
      onAddNewClassSuccess();
      showToast('New class added successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error adding new class',
        SnackbarSeverityType.error
      );
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await addClass();
      handleModalClose();
    } finally {
      setIsLoading(false);
    }
  };
  const role = staffDetailData?.role || '';

  const isFormValid =
    role === UserRole.SCHOOL_OFFICE ? section : grade && division;

  const disableSubjectDropdown =
    !grade || !division || subjectsLoading || subjectsOptions.length === 0;

  return (
    <DialogModal
      open={open}
      onClose={handleModalClose}
      title='Add Class'
      maxWidth='sm'
      actions={
        <>
          <Button
            onClick={handleModalClose}
            color='secondary'
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color='primary'
            variant='contained'
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Add'}
          </Button>
        </>
      }
    >
      <ClassFormWrapper>
        <DropdownSelect
          label='Academic Year'
          name='academicYear'
          value={academicYear}
          options={academicYears}
          onChange={(event) => setAcademicYear(event.target.value as string)}
          required
          fullWidth
          hasTopLabel
        />
        {(role === UserRole.TEACHER || role === UserRole.COORDINATOR) && (
          <>
            <DropdownSelect
              label='Grade'
              name='grade'
              placeholder='Select Grade'
              value={grade}
              options={gradeOptions}
              onChange={(event) => setGrade(event.target.value as string)}
              required
              fullWidth
              hasTopLabel
            />
            <DropdownSelect
              label='Division'
              name='division'
              placeholder='Select Division'
              value={division}
              options={divisionOptions}
              onChange={(event) => setDivision(event.target.value as string)}
              required
              fullWidth
              hasTopLabel
            />
          </>
        )}
        {role === UserRole.TEACHER && (
          <>
            <SelectAll
              label='Subjects'
              placeholder='Select Subjects'
              options={subjectsOptions}
              hasTopLabel
              onChange={setSubject}
              selected={subject}
              sx={{
                width: '300px'
              }}
              disabled={disableSubjectDropdown}
              formControlMargin='none'
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={isClassTeacher}
                onChange={(e) => setIsClassTeacher(e.target.checked)}
                color='primary'
              />
              <Typography variant='h6'>Class Teachers</Typography>
            </Box>
          </>
        )}
        {role === UserRole.SCHOOL_OFFICE && (
          <DropdownSelect
            label='Section'
            name='section'
            value={section}
            options={sectionOptions}
            onChange={(event) => setSection(event.target.value as string)}
            required
            fullWidth
            hasTopLabel
          />
        )}
      </ClassFormWrapper>
    </DialogModal>
  );
};

export default AddClassForm;
